import {
  SESSIONS_TEST_IDS,
  SESSION_LAYER_LABEL,
  type TurnLayer,
} from '@/components/admin/sessions/contracts';
import { Icon } from '@pms-digital/design-system';
import type { ReactNode } from 'react';
import { LAYER_INSET } from './sources';

/**
 * ONE accordion under a turn - the shell the five layers of §12.3 wear.
 *
 * ## A disclosure BUTTON and not a `<details>`
 *
 * The mockup draws `<details>` and the decision it carries is about the DOM, not
 * about the element: « au repos, rien n'est rendu - pas même un titre
 * d'accordéon replié », because a closed `<details>` still shows its summary
 * line and five of those per turn is five lines of noise on forty turns. This
 * component honours that by not being mounted at all until a layer is revealed;
 * what remains is a disclosure, and a `button` + `aria-expanded` is the form
 * every other fold of this application already takes (`protocol-problems`,
 * `filter-bar`, `objective-row`). It is also the form jsdom can actually be
 * driven through, which `<details>` is not.
 *
 * ## The hint is not decoration
 *
 * It is what the layer says WITHOUT being opened - « 2 appels · 0,013 € », « un
 * garde-fou détecté » - and it is the reason a reader can walk forty turns with
 * one layer revealed and stop on the one that matters. A hint that repeated the
 * title would cost a line and answer nothing.
 */

export interface TurnLayerShellProps {
  layer: TurnLayer;
  /** What the layer answers in one line, read without opening it. */
  hint: string;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}

export function TurnLayerShell({ layer, hint, open, onToggle, children }: TurnLayerShellProps) {
  return (
    <div
      data-testid={SESSIONS_TEST_IDS.layer}
      data-layer={layer}
      data-open={open}
      className="border-gray-100 border-t"
    >
      <button
        type="button"
        aria-expanded={open}
        onClick={onToggle}
        className={`flex w-full cursor-pointer items-center gap-2 py-2 text-left font-semibold text-gray-600 text-caption uppercase tracking-wide hover:text-bleu-nuit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-bleu-nuit ${LAYER_INSET}`}
      >
        <span
          className={`inline-flex shrink-0 transition-transform motion-reduce:transition-none ${
            open ? 'rotate-90' : ''
          }`}
        >
          <Icon name="chevron_right" size={14} />
        </span>
        <span className="shrink-0">{SESSION_LAYER_LABEL[layer]}</span>
        <span className="min-w-0 truncate font-normal text-gray-500 normal-case tracking-normal">
          · {hint}
        </span>
      </button>
      {open ? (
        <div
          className={`flex flex-col gap-2.5 pt-0.5 pb-3.5 text-gray-700 text-small ${LAYER_INSET}`}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
}

/**
 * The two-column line every layer uses for « libellé / valeur ».
 *
 * It emits a `dt`/`dd` pair, so it goes INSIDE a `<dl>` - the caller owns the
 * list because a layer draws several of these and one `dl` per pair would be a
 * description list of one term each.
 */
export function LayerLine({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="grid gap-1 sm:grid-cols-[9.5rem_minmax(0,1fr)] sm:gap-3">
      <dt className="text-gray-600">{label}</dt>
      <dd className="m-0 min-w-0 break-words text-gray-900">{children}</dd>
    </div>
  );
}
